import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { KeyRound, Check, Trash2, AlertTriangle, Eye, Gauge } from 'lucide-react'
import apiClient from '@/services/api'
import { useToast } from '@/contexts/ToastContext'

/**
 * Claves de API, editables sin entrar al servidor.
 *
 * El valor NUNCA vuelve del servidor: se recibe una máscara, de dónde sale y cuándo se
 * tocó. Por eso el campo de escritura siempre empieza vacío — no es que no se cargue, es
 * que no hay nada que cargar, y eso es deliberado.
 */

interface SecretStatus {
  name: string
  masked: string | null
  configured: boolean
  source: 'db' | 'env' | null
  updatedAt?: string
  updatedBy?: string
}

interface SaldoClave {
  masked: string
  creditCount: number | null
  error: string | null
  apartadaHasta: string | null
  motivo: string | null
}

const DESCRIPCION: Record<string, { titulo: string; para: string; donde: string }> = {
  SCRAPECREATORS_API_KEY: {
    titulo: 'ScrapeCreators',
    para:
      'Todas las búsquedas y el minero. Sin ella el sistema no encuentra anuncios. ' +
      'Puedes pegar varias separadas por coma o por salto de línea: cuando una se queda ' +
      'sin créditos, la siguiente llamada usa otra automáticamente.',
    donde: 'scrapecreators.com'
  },
  OPENROUTER_API_KEY: {
    titulo: 'OpenRouter',
    para: 'Clasifica los productos y destripa la oferta de los ganadores.',
    donde: 'openrouter.ai'
  },
  TELEGRAM_BOT_TOKEN: {
    titulo: 'Telegram',
    para: 'Avisos del monitor diario.',
    donde: '@BotFather'
  },
  FACEBOOK_ACCESS_TOKEN: {
    titulo: 'Facebook',
    para: 'Respaldo para algunas consultas a la biblioteca de anuncios.',
    donde: 'developers.facebook.com'
  }
}

export default function ApiKeysPanel() {
  const { showSuccess, showError } = useToast()
  const queryClient = useQueryClient()
  const [borradores, setBorradores] = useState<Record<string, string>>({})

  const { data, isLoading } = useQuery('secrets', async () => {
    const r = await apiClient.get('/secrets')
    return r.data
  })

  // Se carga al abrir: sin la lista de claves no se puede quitar una sola, y preguntar el
  // saldo no cuesta créditos.
  const saldo = useQuery<{ keys: SaldoClave[] }>(
    'scrapecreators-balance',
    async () => (await apiClient.get('/secrets/scrapecreators/balance')).data,
    { refetchOnWindowFocus: false }
  )

  const invalidar = () => {
    queryClient.invalidateQueries('secrets')
    queryClient.invalidateQueries('scrapecreators-balance')
  }

  const guardar = useMutation(
    ({ name, value }: { name: string; value: string }) =>
      // ScrapeCreators SUMA claves en vez de reemplazarlas: como el navegador nunca ve las
      // guardadas, un reemplazo obligaría a reescribirlas todas para añadir una.
      name === 'SCRAPECREATORS_API_KEY'
        ? apiClient.post('/secrets/scrapecreators/keys', { value })
        : apiClient.put(`/secrets/${name}`, { value }),
    {
      onSuccess: (r, { name }) => {
        showSuccess(r.data?.message || 'Clave guardada')
        // Se limpia el campo: dejar la clave escrita en pantalla no aporta nada y la deja
        // expuesta a cualquiera que pase por detrás.
        setBorradores(b => ({ ...b, [name]: '' }))
        invalidar()
      },
      onError: (e: any) => showError(e?.response?.data?.message || 'No se pudo guardar')
    }
  )

  const quitarUna = useMutation(
    (masked: string) =>
      apiClient.delete(`/secrets/scrapecreators/keys?masked=${encodeURIComponent(masked)}`),
    {
      onSuccess: r => {
        showSuccess(r.data?.message || 'Clave quitada')
        invalidar()
      },
      onError: (e: any) => showError(e?.response?.data?.message || 'No se pudo quitar')
    }
  )

  const borrar = useMutation((name: string) => apiClient.delete(`/secrets/${name}`), {
    onSuccess: r => {
      showSuccess(r.data?.message || 'Clave borrada')
      invalidar()
    },
    onError: (e: any) => showError(e?.response?.data?.message || 'No se pudo borrar')
  })

  const secrets: SecretStatus[] = data?.secrets || []

  return (
    <div className="rounded-xl border border-stone-700 bg-stone-900 p-5">
      <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-stone-100">
        <KeyRound className="h-4 w-4" />
        Claves de API
      </h2>
      <p className="mb-4 text-xs text-stone-400">
        Se guardan cifradas y surten efecto en la siguiente llamada, sin reiniciar nada. Una
        vez guardada no se puede volver a leer desde aquí: solo reemplazar o borrar. Las de
        ScrapeCreators se suman a las que ya hay, y cada una se quita por separado.
      </p>

      {isLoading && <p className="text-sm text-stone-500">Cargando…</p>}

      <div className="space-y-4">
        {secrets.map(s => {
          const info = DESCRIPCION[s.name] || { titulo: s.name, para: '', donde: '' }
          const borrador = borradores[s.name] ?? ''
          const esScrapeCreators = s.name === 'SCRAPECREATORS_API_KEY'

          return (
            <div key={s.name} className="rounded-lg border border-stone-800 bg-stone-950/50 p-4">
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-stone-100">{info.titulo}</span>

                {s.configured ? (
                  <span
                    className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                    style={{ background: '#34d3991a', color: '#34d399' }}
                  >
                    <Check className="h-3 w-3" />
                    {s.masked}
                  </span>
                ) : (
                  <span
                    className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                    style={{ background: '#fb71851a', color: '#fb7185' }}
                  >
                    <AlertTriangle className="h-3 w-3" />
                    sin configurar
                  </span>
                )}

                {/* De dónde sale importa: si viene del entorno, editarla aquí la sustituye. */}
                {s.source && (
                  <span className="text-xs text-stone-600">
                    {s.source === 'db' ? 'guardada aquí' : 'viene del entorno del servidor'}
                  </span>
                )}

                {s.updatedAt && (
                  <span className="text-xs text-stone-600">
                    · cambiada el {new Date(s.updatedAt).toLocaleDateString('es-ES')}
                    {s.updatedBy ? ` por ${s.updatedBy}` : ''}
                  </span>
                )}
              </div>

              {info.para && <p className="mb-3 text-xs text-stone-500">{info.para}</p>}

              <form
                className="flex flex-wrap gap-2"
                onSubmit={e => {
                  e.preventDefault()
                  const value = borrador.trim()
                  if (!value) return showError('Escribe la clave antes de guardar')
                  guardar.mutate({ name: s.name, value })
                }}
              >
                {/* Varias claves no caben en un input de una línea: ahí se escriben a ciegas
                    y no se ve dónde acaba una y empieza la otra. */}
                {esScrapeCreators ? (
                  <textarea
                    rows={3}
                    value={borrador}
                    onChange={e => setBorradores(b => ({ ...b, [s.name]: e.target.value }))}
                    placeholder={
                      s.configured
                        ? 'Otra clave más (una por línea). Las de arriba se conservan'
                        : 'Una clave por línea'
                    }
                    autoComplete="off"
                    spellCheck={false}
                    className="min-w-[240px] flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 font-mono text-sm text-stone-100 placeholder-stone-600 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
                  />
                ) : (
                  <input
                    type="password"
                    value={borrador}
                    onChange={e => setBorradores(b => ({ ...b, [s.name]: e.target.value }))}
                    placeholder={s.configured ? 'Escribe una nueva para reemplazarla' : `Clave de ${info.donde}`}
                    autoComplete="off"
                    spellCheck={false}
                    className="min-w-[240px] flex-1 rounded-lg border border-stone-700 bg-stone-950 px-3 py-2 font-mono text-sm text-stone-100 placeholder-stone-600 focus:border-secondary-400 focus:ring-2 focus:ring-secondary-500/30"
                  />
                )}

                <button
                  type="submit"
                  disabled={guardar.isLoading || !borrador.trim()}
                  className="rounded-lg bg-secondary-600 px-4 py-2 text-sm font-medium text-white hover:bg-secondary-500 disabled:opacity-40"
                >
                  {esScrapeCreators ? 'Añadir' : s.configured ? 'Reemplazar' : 'Guardar'}
                </button>

                {s.source === 'db' && (
                  <button
                    type="button"
                    onClick={() => borrar.mutate(s.name)}
                    disabled={borrar.isLoading}
                    title={
                      esScrapeCreators
                        ? 'Borrar TODAS las guardadas aquí y volver a la del entorno, si la hay'
                        : 'Borrar la guardada aquí y volver a la del entorno, si la hay'
                    }
                    className="rounded-lg bg-stone-800 px-3 py-2 text-stone-400 hover:bg-stone-700 hover:text-stone-200 disabled:opacity-40"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </form>

              {esScrapeCreators && (
                <div className="mt-3 border-t border-stone-800 pt-3">
                  <button
                    type="button"
                    onClick={() => saldo.refetch()}
                    disabled={saldo.isFetching}
                    className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-200 disabled:opacity-40"
                  >
                    <Gauge className="h-3.5 w-3.5" />
                    {saldo.isFetching ? 'Consultando…' : 'Actualizar créditos de cada clave'}
                  </button>

                  {saldo.isError && (
                    <p className="mt-2 text-xs text-rose-400">No se pudo consultar el saldo.</p>
                  )}

                  {saldo.data && (
                    <ul className="mt-2 space-y-1">
                      {saldo.data.keys.length === 0 && (
                        <li className="text-xs text-stone-500">No hay ninguna clave configurada.</li>
                      )}
                      {saldo.data.keys.map(k => (
                        <li key={k.masked} className="flex flex-wrap items-center gap-2 text-xs">
                          <span className="font-mono text-stone-300">{k.masked}</span>
                          {k.error ? (
                            <span className="text-rose-400">{k.error}</span>
                          ) : (
                            <span
                              className={
                                (k.creditCount ?? 0) > 0 ? 'text-emerald-400' : 'text-rose-400'
                              }
                            >
                              {k.creditCount} créditos
                            </span>
                          )}
                          {/* Que esté apartada explica por qué el sistema no la está usando. */}
                          {k.apartadaHasta && (
                            <span className="text-stone-500">
                              · en reposo por {k.motivo} hasta{' '}
                              {new Date(k.apartadaHasta).toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          )}

                          {/* Quitar una sola: sin esto, cambiar una clave agotada obligaría a
                              borrarlas todas y volver a escribir las que sí funcionan. */}
                          <button
                            type="button"
                            onClick={() => quitarUna.mutate(k.masked)}
                            disabled={quitarUna.isLoading}
                            title="Quitar solo esta clave"
                            aria-label={`Quitar la clave ${k.masked}`}
                            className="text-stone-600 hover:text-rose-400 disabled:opacity-40"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <p className="mt-4 flex items-start gap-2 text-xs text-stone-600">
        <Eye className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Se cifran antes de guardarse, así que un volcado de la base no las revela. La llave
          que las abre sigue en el entorno del servidor: quien tenga acceso ahí puede
          descifrarlas, y eso no lo arregla ningún panel.
        </span>
      </p>
    </div>
  )
}
